import './ProcessLogs.scss';
import cn from 'classnames';
import { useMemo, useState } from 'react';
import { ListGroup, Modal } from 'react-bootstrap';
import { v4 as uuidv4 } from 'uuid';
import { formatMoney } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { Button } from '@Components/Button';
import { DatePicker } from '@Components/DatePicker';
import { Intent, Size } from '@Components/designConstants';
import { Checkbox } from '@Components/FormCheck';
import { Page } from '@Components/Page';
import { BPMTable, CellRenderer, TypedHeader } from '@Components/StickyTable';
import { useSetError } from '@src/redux/modals';
import { isNotMonday } from '@src/utils/date-utils';
import {
  awaitJSON,
  DagLambdaFetch,
  LinearBuyingLambdaFetch,
  pollS3,
  S3Put
} from '@src/utils/fetch-utils';
import {
  CUTOFF_DATE,
  DownloadTarget,
  getDefaultWeek,
  PAGE_TITLE,
  PARSE_POST_LOGS_JOB_CONFIG,
  PARSE_POST_LOGS_JOB_PARAMS,
  ProcessedPostLogRow,
  RawPostLogRow,
  S3_BUCKET_LOCATION
} from './process-logs-constants';
import {
  aggregatePostLogs,
  computePostLogTotals,
  getAcceptKey,
  getUniqueCompanies
} from './process-logs-utils';

const isOutsideCutoff = (date: string) => date > CUTOFF_DATE;

interface ProcessLogsActionsProps {
  week: string;
  onWeekChange: (week: string) => void;
  uploading: boolean;
  hasFiles: boolean;
  hasProcessedData: boolean;
  hasAcceptable: boolean;
  onUploadAll: () => void;
  onClear: () => void;
  onAccept: () => void;
}

const ProcessLogsActions = ({
  week,
  onWeekChange,
  uploading,
  hasFiles,
  hasProcessedData,
  hasAcceptable,
  onUploadAll,
  onClear,
  onAccept
}: ProcessLogsActionsProps) => (
  <div className='process-logs-actions'>
    <Button
      size={Size.LG}
      design={Intent.SUCCESS}
      onClick={onUploadAll}
      disabled={uploading || !hasFiles}
      loading={uploading}
    >
      Upload and Process
    </Button>
    <Button size={Size.LG} design={Intent.ERROR} onClick={onClear} disabled={!hasFiles}>
      Clear
    </Button>
    {hasProcessedData && (
      <Button size={Size.LG} design={Intent.SUCCESS} disabled={!hasAcceptable} onClick={onAccept}>
        Accept
      </Button>
    )}
    <DatePicker
      isSingle
      fullWeeksOnly
      removeHeaderText
      range={{ start: week, end: week }}
      isOutsideRange={isOutsideCutoff}
      isDayBlocked={isNotMonday}
      onChange={({ start }) => onWeekChange(start)}
    />
  </div>
);

interface UploadTableProps {
  fileData: File[];
  onRemoveFile: (name: string) => void;
}

const UploadTable = ({ fileData, onRemoveFile }: UploadTableProps) => {
  const headers: TypedHeader<File>[] = useMemo(
    () => [
      { label: 'Name', name: 'name', flex: 1, nonInteractive: true },
      {
        label: 'Size',
        name: 'size',
        width: 250,
        nonInteractive: true,
        renderer: file => `${(file.size / 1_000_000).toLocaleString()} MB`
      },
      {
        label: 'Actions',
        width: 250,
        nonInteractive: true,
        renderer: file => (
          <Button size={Size.LG} design={Intent.ERROR} onClick={() => onRemoveFile(file.name)}>
            Remove
          </Button>
        )
      }
    ],
    [onRemoveFile]
  );

  return <BPMTable data={fileData} filterBar={false} headers={headers} />;
};

interface SummaryTableProps {
  processedTableData: ProcessedPostLogRow[];
}

const SummaryTable = ({ processedTableData }: SummaryTableProps) => {
  const totals = useMemo(() => computePostLogTotals(processedTableData), [processedTableData]);

  const headers: TypedHeader<ProcessedPostLogRow>[] = useMemo(
    () => [
      { label: 'Company', name: 'company', flex: 1, nonInteractive: true },
      { label: 'Network', name: 'network', width: 150, nonInteractive: true },
      { label: 'Satellite Network', name: 'satelliteNetwork', width: 150, nonInteractive: true },
      { label: 'Length', name: 'length', width: 150, nonInteractive: true },
      { label: 'Count', name: 'count', width: 150, nonInteractive: true },
      {
        label: 'Spend',
        name: 'spend',
        width: 150,
        nonInteractive: true,
        renderer: row => formatMoney(row.spend)
      }
    ],
    []
  );

  const formatTotal = (data: Element | number | string | undefined, columnIndex: number) => {
    if (typeof data !== 'number') return '';
    return headers[columnIndex]?.name === 'spend' ? formatMoney(data) : data.toLocaleString();
  };

  const totalsRenderer: CellRenderer<Element | number | string | undefined> = ({
    data,
    style,
    classes,
    columnIndex
  }) => (
    <div style={style} className={cn(classes, 'grand-total-cell')}>
      {formatTotal(data, columnIndex)}
    </div>
  );

  return (
    <BPMTable
      data={processedTableData}
      totals={{ count: totals.count, spend: totals.spend }}
      totalsRenderer={totalsRenderer}
      filterBar={false}
      headers={headers}
    />
  );
};

interface DownloadPaneProps {
  onFilesSelected: (files: File[]) => void;
  processedTableData?: ProcessedPostLogRow[];
  companies: string[];
  downloadingLog: Record<string, boolean>;
  onDownload: (target: string) => void;
}

const DownloadPane = ({
  onFilesSelected,
  processedTableData,
  companies,
  downloadingLog,
  onDownload
}: DownloadPaneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  return (
    <div className='right-pane'>
      <label
        htmlFor='process-logs-file-input'
        className={cn('file-input-trigger', { dragging: isDragging })}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
          onFilesSelected(Array.from(e.dataTransfer.files));
        }}
      >
        Upload file(s) or drag and drop
        <input
          id='process-logs-file-input'
          data-testid='process-logs-file-input'
          className='file-input-hidden'
          type='file'
          multiple
          onChange={e => {
            const { files } = e.target;
            if (!files) return;
            onFilesSelected(Array.from(files));
          }}
        />
      </label>

      {processedTableData && (
        <div className='log-download-container'>
          <ListGroup className='log-download-list'>
            <ListGroup.Item className='title'>Post-Logs</ListGroup.Item>
            <ListGroup.Item className='list-item'>
              <Button
                size={Size.LG}
                className='log-download-button'
                loading={downloadingLog[DownloadTarget.EXCEL]}
                onClick={() => onDownload(DownloadTarget.EXCEL)}
              >
                Excel
              </Button>
            </ListGroup.Item>
            <ListGroup.Item className='list-item'>
              <Button
                size={Size.LG}
                className='log-download-button'
                loading={downloadingLog[DownloadTarget.CSV]}
                onClick={() => onDownload(DownloadTarget.CSV)}
              >
                CSV
              </Button>
            </ListGroup.Item>
            {companies.map(company => (
              <ListGroup.Item key={company} className='list-item'>
                <Button
                  size={Size.LG}
                  className='log-download-button'
                  loading={downloadingLog[company]}
                  onClick={() => onDownload(company)}
                >
                  {company}
                </Button>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

interface AcceptCompanyModalProps {
  companies: string[];
  acceptedCompanies: Set<string>;
  accepting: boolean;
  onHide: () => void;
  onAccept: (companies: string[]) => void;
}

const AcceptCompanyModal = ({
  companies,
  acceptedCompanies,
  accepting,
  onHide,
  onAccept
}: AcceptCompanyModalProps) => {
  const [selected, setSelected] = useState<string[]>([]);

  const selectable = companies.filter(company => !acceptedCompanies.has(company));
  // `selected` can still hold companies accepted earlier in a partially failed batch.
  const pending = selected.filter(company => !acceptedCompanies.has(company));
  const allSelected = selectable.length > 0 && selectable.every(c => pending.includes(c));

  const toggle = (company: string) =>
    setSelected(current =>
      current.includes(company) ? current.filter(c => c !== company) : [...current, company]
    );

  return (
    <Modal show onHide={accepting ? undefined : onHide}>
      <Modal.Header closeButton={!accepting}>
        <Modal.Title>Accept Spots for Companies</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='accept-company-list'>
          <Checkbox
            id='process-logs-accept-all'
            label='Select all'
            checked={allSelected}
            indeterminate={pending.length > 0 && !allSelected}
            disabled={accepting || selectable.length === 0}
            onChange={() => setSelected(allSelected ? [] : selectable)}
          />
          {companies.map(company => {
            const accepted = acceptedCompanies.has(company);
            return (
              <Checkbox
                key={company}
                id={`process-logs-accept-${company}`}
                label={accepted ? `${company} (accepted)` : company}
                checked={pending.includes(company)}
                disabled={accepted || accepting}
                onChange={() => toggle(company)}
              />
            );
          })}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          loading={accepting}
          disabled={pending.length === 0}
          onClick={() => onAccept(pending)}
        >
          Accept
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();

    fileReader.onload = () => {
      const data = fileReader.result;
      if (typeof data !== 'string') {
        reject(new Error(`Couldn't read ${file.name}`));
        return;
      }
      const [, encodedData] = data.split(',');
      resolve(encodedData);
    };
    fileReader.onerror = () => reject(fileReader.error ?? new Error(`Couldn't read ${file.name}`));

    fileReader.readAsDataURL(file);
  });

export const ProcessLogs = () => {
  const setError = useSetError();
  const [week, setWeek] = useState(getDefaultWeek);
  const [fileData, setFileData] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [processedTableData, setProcessedTableData] = useState<ProcessedPostLogRow[]>();
  const [acceptModal, setAcceptModal] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [acceptedKeys, setAcceptedKeys] = useState<Set<string>>(new Set());
  const [s3Id, setS3Id] = useState('');
  const [downloadingLog, setDownloadingLog] = useState<Record<string, boolean>>({});

  const uploadToS3 = async (file: File, id: string) => {
    const encodedData = await readFileAsBase64(file);
    await S3Put(S3_BUCKET_LOCATION, `${id}/input/${file.name}`, encodedData, {
      bufferEncoding: 'base64',
      contentType: file.type
    });
  };

  const uploadAll = async () => {
    setUploading(true);
    try {
      const id = uuidv4();

      await Promise.all(fileData.map(file => uploadToS3(file, id)));

      await DagLambdaFetch('/start_ingestion_job', {
        method: 'POST',
        body: {
          taskBody: {
            params: { ...PARSE_POST_LOGS_JOB_PARAMS, extra: `${id},${week}` },
            ...PARSE_POST_LOGS_JOB_CONFIG
          }
        }
      });

      const postlogsBlob = await pollS3({
        bucket: S3_BUCKET_LOCATION,
        filename: `${id}/output/postlogs.json`,
        mimeType: 'application/json',
        autoDownload: false,
        noTimeout: true
      });

      const rawRows = await awaitJSON<RawPostLogRow[]>(postlogsBlob);

      // Only commit the new build's id once the whole sequence has succeeded, so it stays in
      // sync with processedTableData -- a failure here must leave s3Id pointing at whichever
      // build is still shown (if any), not an id whose upload/ingest never completed.
      setS3Id(id);
      setProcessedTableData(aggregatePostLogs(rawRows));
      setFileData([]);
    } catch (e) {
      setError({ message: `Couldn't process logs: ${e.message}`, reportError: e });
    } finally {
      setUploading(false);
    }
  };

  // Accepts serially, stopping at the first failure. Each success is recorded immediately so a
  // company that went through can never be re-submitted, even if a later one fails.
  const acceptCompanies = async (companiesToAccept: string[]) => {
    setAccepting(true);
    for (const company of companiesToAccept) {
      try {
        await LinearBuyingLambdaFetch('/reconcile_accept', {
          method: 'POST',
          body: {
            build_id: s3Id,
            company,
            week
          }
        });
        setAcceptedKeys(current => new Set(current).add(getAcceptKey(s3Id, week, company)));
      } catch (e) {
        setError({ message: `Couldn't accept spots for ${company}: ${e.message}`, reportError: e });
        setAccepting(false);
        return;
      }
    }
    setAccepting(false);
    setAcceptModal(false);
  };

  const removeLog = (name: string) => {
    setFileData(current => current.filter(file => file.name !== name));
  };

  const downloadLog = async (target: string) => {
    setDownloadingLog(current => ({ ...current, [target]: true }));
    try {
      if (target === DownloadTarget.EXCEL) {
        await pollS3({
          bucket: S3_BUCKET_LOCATION,
          filename: `${s3Id}/output/postlogs.xlsx`,
          mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      } else if (target === DownloadTarget.CSV) {
        await pollS3({
          bucket: S3_BUCKET_LOCATION,
          filename: `${s3Id}/output/postlogs.csv`,
          mimeType: 'text/csv'
        });
      } else {
        await pollS3({
          bucket: S3_BUCKET_LOCATION,
          filename: `${s3Id}/output/${target}_${week}_postlogs.csv`,
          mimeType: 'text/csv'
        });
      }
    } catch (e) {
      setError({ message: `Couldn't download ${target} log: ${e.message}`, reportError: e });
    } finally {
      setDownloadingLog(current => {
        const { [target]: _removed, ...rest } = current;
        return rest;
      });
    }
  };

  const companies = useMemo(
    () => getUniqueCompanies(processedTableData ?? []),
    [processedTableData]
  );
  const acceptedCompanies = useMemo(
    () => new Set(companies.filter(company => acceptedKeys.has(getAcceptKey(s3Id, week, company)))),
    [companies, acceptedKeys, s3Id, week]
  );

  return (
    <Page
      title={PAGE_TITLE}
      pageType={PAGE_TITLE}
      actions={
        <ProcessLogsActions
          week={week}
          onWeekChange={setWeek}
          uploading={uploading}
          hasFiles={fileData.length > 0}
          hasProcessedData={Boolean(processedTableData)}
          hasAcceptable={acceptedCompanies.size < companies.length}
          onUploadAll={uploadAll}
          onClear={() => setFileData([])}
          onAccept={() => setAcceptModal(true)}
        />
      }
    >
      <div className='process-logs-container'>
        <div className='left-pane'>
          <div
            data-testid='process-logs-upload-table'
            className={cn('upload-table', { 'summary-not-active': !processedTableData })}
          >
            <UploadTable fileData={fileData} onRemoveFile={removeLog} />
          </div>
          <div
            data-testid='process-logs-summary-table'
            className={cn('summary-table', { active: Boolean(processedTableData) })}
          >
            {processedTableData && <SummaryTable processedTableData={processedTableData} />}
          </div>
        </div>

        <DownloadPane
          onFilesSelected={files => setFileData(current => [...current, ...files])}
          processedTableData={processedTableData}
          companies={companies}
          downloadingLog={downloadingLog}
          onDownload={downloadLog}
        />

        {acceptModal && (
          <AcceptCompanyModal
            companies={companies}
            acceptedCompanies={acceptedCompanies}
            accepting={accepting}
            onHide={() => setAcceptModal(false)}
            onAccept={acceptCompanies}
          />
        )}
      </div>
    </Page>
  );
};
